import { zodResolver } from '@hookform/resolvers/zod';
import { ColorSchema, type Priority, PriorityUrgencySchema } from '@servicetim/shared';
import { useCreatePriority, useUpdatePriority } from '@servicetim/ui/api';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { problemDetail } from '../lib/field-errors';
import { applyProblemErrors } from '../lib/rhf-errors';
import { ColorInput } from '../ui/color-input';
import { FormRow } from '../ui/form-row';
import { SimpleSelect } from '../ui/simple-select';

const PriorityFormSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(40),
  color: ColorSchema.nullable(),
  urgency: PriorityUrgencySchema,
  isDefault: z.boolean(),
});
type PriorityFormValues = z.infer<typeof PriorityFormSchema>;

const toValues = (p: Priority | null): PriorityFormValues => ({
  name: p?.name ?? '',
  color: p?.color ?? null,
  urgency: p?.urgency ?? 'low',
  isDefault: p?.isDefault ?? false,
});

const URGENCY_OPTIONS = [
  { value: 'high', label: 'High (breaches escalate faster)' },
  { value: 'low', label: 'Low' },
];

export interface PriorityDialogProps {
  projectId: string;
  priority: Priority | null;
  /** Rank given to a new priority (appended last). */
  nextRank: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PriorityDialog({
  projectId,
  priority,
  nextRank,
  open,
  onOpenChange,
}: PriorityDialogProps) {
  const id = useId();
  const create = useCreatePriority(projectId);
  const update = useUpdatePriority(projectId);
  const [omitPaths, setOmitPaths] = useState<Set<string>>(new Set());
  const form = useForm<PriorityFormValues>({
    resolver: zodResolver(PriorityFormSchema),
    defaultValues: toValues(priority),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the dialog opens or the row changes
  useEffect(() => {
    if (open) {
      form.reset(toValues(priority));
      create.reset();
      update.reset();
      setOmitPaths(new Set());
    }
  }, [open, priority]);

  const error = create.error ?? update.error;
  const detail = problemDetail(error);
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      if (priority) {
        await update.mutateAsync({
          id: priority.id,
          patch: {
            name: values.name,
            color: values.color,
            urgency: values.urgency,
            ...(values.isDefault && !priority.isDefault ? { isDefault: true } : {}),
          },
        });
        toast.success(`Priority "${values.name}" updated`);
      } else {
        await create.mutateAsync({ ...values, rank: nextRank });
        toast.success(`Priority "${values.name}" created`);
      }
      onOpenChange(false);
    } catch (caught) {
      setOmitPaths(
        applyProblemErrors(caught, form.setError, {
          known: ['name', 'color', 'urgency', 'isDefault'],
        }),
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{priority ? `Edit ${priority.name}` : 'New priority'}</DialogTitle>
          <DialogDescription>
            Rank order is set from the table; the default priority is applied to tickets created
            without one.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(event);
          }}
        >
          {error && detail !== 'priority.name_taken' ? (
            <ProblemBanner error={error} omitPaths={omitPaths} size="sm" />
          ) : null}
          <FormRow
            label="Name"
            htmlFor={`${id}-name`}
            required
            error={
              detail === 'priority.name_taken'
                ? 'Another priority has this name'
                : errors.name?.message
            }
          >
            <Input id={`${id}-name`} {...form.register('name')} maxLength={40} autoFocus />
          </FormRow>
          <Controller
            control={form.control}
            name="color"
            render={({ field, fieldState }) => (
              <FormRow label="Color" htmlFor={`${id}-color`} error={fieldState.error?.message}>
                <ColorInput
                  id={`${id}-color`}
                  value={field.value}
                  onChange={field.onChange}
                  invalid={fieldState.invalid}
                />
              </FormRow>
            )}
          />
          <Controller
            control={form.control}
            name="urgency"
            render={({ field }) => (
              <FormRow label="Urgency" htmlFor={`${id}-urgency`}>
                <SimpleSelect
                  id={`${id}-urgency`}
                  value={field.value}
                  onChange={(next) => next && field.onChange(next)}
                  options={URGENCY_OPTIONS}
                />
              </FormRow>
            )}
          />
          <Controller
            control={form.control}
            name="isDefault"
            render={({ field }) => (
              <FormRow
                label="Default priority"
                htmlFor={`${id}-default`}
                inline
                hint={
                  priority?.isDefault
                    ? 'To change the default, set it on another priority.'
                    : 'Setting a new default clears the previous one.'
                }
              >
                <Checkbox
                  id={`${id}-default`}
                  checked={field.value}
                  disabled={priority?.isDefault ?? false}
                  onCheckedChange={(next) => field.onChange(next === true)}
                />
              </FormRow>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {priority ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
